import { TimeOption } from '@amzn/aet-galactus-kit';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import { StateContext } from '../../../../../GlobalStateStore';
import { renderUrl, urls } from '../../../../../Util';
import {
  Feature,
  isFeatureEnabled,
} from '../../../../../config/FeaturesConfig';
import {
  NoDataPrinter,
  parseFixedString,
  SUBMETRIC_FONT_SIZE,
} from '../helper';

interface DisplaySubMetricProps {
  value: string | null;
  isPercentage?: boolean;
  isTime?: boolean;
  isBold?: boolean;
  isHour?: boolean;
  datapointNull?: boolean;
  isLink?: boolean;
  vertical?: string;
  skill?: string;
  segment?: string;
  channel?: string;
  marketplace?: string;
  timeOption?: TimeOption;
  timestamp?: string;
  tabRef?: string;
  topLevelMetric?: string;
}

const DisplaySubmetric: React.FC<DisplaySubMetricProps> = ({
  value,
  isPercentage,
  isTime,
  isBold,
  isHour,
  datapointNull,
  isLink,
  vertical,
  skill,
  segment,
  channel,
  marketplace,
  timeOption,
  timestamp,
  tabRef,
  topLevelMetric,
}: DisplaySubMetricProps): React.ReactElement => {
  let parsedString: string;

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;

  const { userID } = inspireOptions.data;

  const routeParams = useParams();
  const navigate = useNavigate();

  const userId = routeParams.userId ? routeParams.userId : userID;

  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);

  if (isTime) {
    if (datapointNull || value === '--') {
      return (
        <span style={{ paddingTop: '10px' }}>
          <Text type={SUBMETRIC_FONT_SIZE}>--</Text>
        </span>
      );
    }
    if (value === null || value === '') return NoDataPrinter(isBold!);

    parsedString = value;
  } else {
    if (datapointNull || value === '--') {
      return (
        <span style={{ paddingTop: '10px' }}>
          <Text type={SUBMETRIC_FONT_SIZE}>--</Text>
        </span>
      );
    }
    if (value === null || value === '') {
      return NoDataPrinter(isBold!);
    }

    const valueFloat = parseFloat(value);
    if (isNaN(valueFloat)) {
      return NoDataPrinter(isBold!);
    }

    parsedString = parseFixedString(valueFloat, isPercentage!, false);
  }
  const textSpan = isBold ? (
    <span
      style={{
        paddingTop: legacyExperience ? '0px' : '10px',
      }}
    >
      <Text type={SUBMETRIC_FONT_SIZE}>
        <b style={{ color: isLink ? '#077398' : '#232F3E' }}>
          {parsedString}
          {isTime && (
            <>
              {isHour ? (
                <>
                  {' '}
                  <FormattedMessage
                    id='cs_perfportal_hour'
                    defaultMessage='hour'
                  />
                </>
              ) : (
                <>
                  {' '}
                  <FormattedMessage
                    id='cs_perfportal_min'
                    defaultMessage='min'
                  />
                </>
              )}
            </>
          )}
        </b>
      </Text>
    </span>
  ) : (
    <span
      style={{
        paddingTop: legacyExperience ? '0px' : '10px',
      }}
    >
      <Text type={SUBMETRIC_FONT_SIZE}>
        {parsedString}
        {isTime && (
          <>
            {isHour ? (
              <>
                {' '}
                <FormattedMessage
                  id='cs_perfportal_hour'
                  defaultMessage='hour'
                />
              </>
            ) : (
              <>
                {' '}
                <FormattedMessage id='cs_perfportal_min' defaultMessage='min' />
              </>
            )}
          </>
        )}
      </Text>
    </span>
  );

  return isLink ? (
    <div
      style={{
        position: 'relative',
        top: legacyExperience ? '0px' : '10px',
        font: '14px',
      }}
    >
      <Link
        type='primary'
        {...(tabRef && tabRef === 'repeat'
          ? {
              'data-csa-c-content-id': `hmd-repeat-contacts-details-${userId}`,
              'data-csa-c-slot-id': 'hmd-repeat-contacts-details',
            }
          : {})}
        {...(tabRef && tabRef === 'hmdYes'
          ? {
              'data-csa-c-content-id': `hmd-yes-contacts-details-for-${userId}`,
              'data-csa-c-slot-id': 'hmd-yes-contacts-details',
            }
          : {})}
        {...(tabRef && tabRef === 'hmdNo'
          ? {
              'data-csa-c-content-id': `hmd-no-contacts-details-for-${userId}`,
              'data-csa-c-slot-id': 'hmd-no-contacts-details',
            }
          : {})}
        data-csa-c-profile-id={`${userId}`}
        {...(topLevelMetric ? { 'data-csa-c-tag3': topLevelMetric } : {})}
        href={renderUrl(`${urls.hmdDetails}/${userId}`, {
          params: {
            vertical: vertical!,
            skill: skill!,
            channel: channel!,
            segment: segment!,
            marketplace: marketplace!,
            timeOption: timeOption!,
            timestamp: timestamp!,
          },
          href: tabRef,
        })}
        onClick={navigate}
        data-csa-c-type='link'
        data-csa-c-slot-id={`lighthouse-metric-rap-${tabRef}-open-HMDDetails-slot`}
        data-csa-c-content-id={`lighthouse-metric-rap-${tabRef}-open-HMDDetails`}
        data-csa-c-tag1='Dashboard'
        data-csa-c-tag2='Metrics'
        data-csa-c-tag3='RAP Expandable'
        data-csa-c-tag4='Sub Metrics'
      >
        {textSpan}
      </Link>
    </div>
  ) : (
    <>{textSpan}</>
  );
};

export default DisplaySubmetric;
